import * as Collapsible from '@radix-ui/react-collapsible'
import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import { Link, useLocation } from '@tanstack/react-router'
import clsx from 'clsx'
import React, { forwardRef, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOnClickOutside } from 'usehooks-ts'

import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react'
import ChevronSmallDown from '@/assets/icons/chevron-small-down.svg?react'
import DeleteIcon from '@/assets/icons/delete.svg?react'
import MoreOutlined from '@/assets/icons/menu-vertical.svg?react'
import UpdateIcon from '@/assets/icons/update.svg?react'
import UserIcon from '@/assets/icons/user.svg?react'
import ButtonIcon from '@/components/ButtonIcon/ButtonIcon'
import Dropdown, { DropdownMenuItem } from '@/components/Dropdown/Dropdown'
import { USER_PROFILE } from '@/constants/user-profile'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import { toast } from '@/hooks/useToast'
import i18n from '@/i18n'
import useRemoveFavourite from '@/mutations/useRemoveFavourite'
import { useSchoolData } from '@/queries/schoolDataQueryOptions'
import useFavourites from '@/queries/useFavourites'
import useAuthStore, { type User } from '@/store/useAuthStore'
import { type UserProfile } from '@/types/user-profile'
import { isFeatureFlagEnabled } from '@/utils/is-feature-flag-enabled'
import { checkIfLocationMatchesString } from '@/utils/location'

import styles from './Navigation.module.scss'
import NavigationSettings from './NavigationSettings'

const getAdministrationRoutes = (user?: User) =>
  [
    {
      title: i18n.t('navigation.teachers-and-users', { ns: 'common' }),
      path: '/users'
    },
    {
      title: i18n.t('navigation.courses-management', { ns: 'common' }),
      path: '/courses-management',
      hidden: () => !user?.isGeneratorAdmin
    },
    {
      title: i18n.t('navigation.curricula', { ns: 'common' }),
      path: '/curricula',
      hidden: () =>
        !user?.isGeneratorAdmin ||
        !isFeatureFlagEnabled('IS_CURRICULUMS_MANAGEMENT_MODULE_ENABLED')
    },
    {
      title: i18n.t('navigation.sections', { ns: 'common' }),
      path: '/sections',
      hidden: () =>
        !user?.isGeneratorAdmin ||
        !isFeatureFlagEnabled('IS_SECTIONS_MODULE_ENABLED')
    },
    {
      title: i18n.t('navigation.rooms', { ns: 'common' }),
      path: '/rooms',
      hidden: () => !user?.isGeneratorAdmin
    },
    {
      title: i18n.t('navigation.availability-management', { ns: 'common' }),
      path: '/availability-management'
    },
    {
      title: i18n.t('navigation.school-settings', { ns: 'common' }),
      path: '/school-settings',
      hidden: () =>
        !isFeatureFlagEnabled('IS_SCHOOL_BREAKS_MODULE_ENABLED') &&
        !isFeatureFlagEnabled('IS_SCHOOL_YEARS_MODULE_ENABLED') &&
        !isFeatureFlagEnabled('IS_SCHOOL_DAY_MODULE_ENABLED')
    }
  ].filter(item => !item.hidden?.())

const getAcademicsRoutes = (user?: User) =>
  [
    {
      title: i18n.t('navigation.timetable', { ns: 'common' }),
      path: '/timetable'
    },
    {
      title: i18n.t('navigation.courses', { ns: 'common' }),
      path: '/courses',
      hidden: () => {
        const TEACHING_PROFILES: UserProfile[] = [
          USER_PROFILE.TUTOR,
          USER_PROFILE.TEACHER
        ]

        return (
          !user?.isSuperAdmin &&
          user?.profiles.every(element => !TEACHING_PROFILES.includes(element))
        )
      }
    },
    {
      title: i18n.t('navigation.students-and-classes', { ns: 'common' }),
      path: '/students-and-classes'
    },
    {
      title: i18n.t('navigation.absences', { ns: 'common' }),
      path: '/absences',
      hidden: () =>
        !user?.profiles.includes(USER_PROFILE.TUTOR) && !user?.isSuperAdmin
    },
    {
      title: i18n.t('navigation.covered-cancelled-lessons', { ns: 'common' }),
      path: '/covered-and-cancelled-lessons',
      hidden: () => !user?.isSuperAdmin
    }
  ].filter(item => !item.hidden?.())

const getItems = (user?: User) => [
  {
    name: i18n.t('navigation.academics', { ns: 'common' }),
    routes: getAcademicsRoutes(user)
  },
  {
    name: i18n.t('navigation.administration', { ns: 'common' }),
    routes: getAdministrationRoutes(user)
  }
]

const Hamburger = forwardRef(function Hamburger(
  props: { onClick: () => void; isOpen: boolean },
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  const { t } = useTranslation(['common'])
  return (
    <ButtonIcon
      ref={ref}
      className={clsx(styles.hamburger, {
        [styles.open]: props.isOpen
      })}
      onClick={() => props.onClick()}
      size="large"
      variant="primary"
      ariaLabel={t('label.open-menu')}
    >
      <span className={clsx(styles.bar, styles.barTop)} aria-hidden="true" />
      <span className={clsx(styles.bar, styles.barMiddle)} aria-hidden="true" />
      <span className={clsx(styles.bar, styles.barBottom)} aria-hidden="true" />
    </ButtonIcon>
  )
})

type NavigationLayoutProps = {
  items: { name: string; routes: { title: string; path: string }[] }[]
  withGeneratorItem?: boolean
}

export const NavigationLayout = (props: NavigationLayoutProps) => {
  const { t } = useTranslation('common')
  const { user } = useAuthStore()

  const [isOpen, setIsOpen] = useState(false)

  const navigationRef = useRef(null)
  const hamburgerRef = useRef(null)
  const languageDropdownRef = useRef(null)

  const handleClickOutside = () => {
    setIsOpen(false)
  }

  const { data: schoolData } = useSchoolData()

  const { isTablet } = useScreenResolution()

  const tabIndexForElement = isTablet && !isOpen ? -1 : 0

  useOnClickOutside(
    [navigationRef, hamburgerRef, languageDropdownRef],
    handleClickOutside
  )

  return (
    <>
      <div
        className={clsx(styles.mobileHeader, isOpen && styles.mobileHeaderOpen)}
      >
        <Hamburger
          isOpen={isOpen}
          ref={hamburgerRef}
          onClick={() => {
            setIsOpen(!isOpen)
          }}
        />
      </div>
      <div className={clsx(styles.overlay, isOpen && styles.overlayVisible)} />

      <div
        className={clsx(styles.navigation, isOpen && styles.navigationIsOpen)}
        aria-hidden={!!(isTablet && !isOpen)}
      >
        <div className={styles.navigationHeaderWrapper}>
          <p className={styles.navigationHeader}>{schoolData?.name}</p>
        </div>

        <div className={styles.navigationWrapper}>
          <NavigationMenu.Root orientation="horizontal" ref={navigationRef}>
            <NavigationMenu.List>
              {props.items.map(({ name, routes }, index) => (
                <NavigationSection
                  key={index}
                  title={name}
                  tabIndex={tabIndexForElement}
                >
                  {routes.map(({ path, title }, index2) => (
                    <NavigationSectionItem
                      key={index2}
                      onClick={() => setIsOpen(false)}
                      path={path}
                      title={title}
                      tabIndex={tabIndexForElement}
                    />
                  ))}
                </NavigationSection>
              ))}
              <Favourites
                onLinkClick={() => setIsOpen(false)}
                tabIndex={tabIndexForElement}
              />
            </NavigationMenu.List>
          </NavigationMenu.Root>
        </div>
        <div className={styles.bottomActions}>
          {props.withGeneratorItem ? (
            <a
              className={styles.bottomMenuItemWrapper}
              href="/generator"
              tabIndex={tabIndexForElement}
            >
              <UpdateIcon className={styles.menuIcon} />

              {t('button.generator')}
            </a>
          ) : null}

          <NavigationSettings
            ref={languageDropdownRef}
            trigger={
              <button
                className={styles.userProfile}
                aria-label={t('help.open-user-settings', {
                  NAME: user?.fullName
                })}
                type="button"
                tabIndex={tabIndexForElement}
              >
                <span className={styles.user}>
                  <span className={styles.avatar} aria-hidden="true">
                    <UserIcon />
                  </span>
                  <span className={styles.username}>{user?.fullName}</span>
                </span>
                <span className={styles.iconContainer} aria-hidden="true">
                  <ArrowRightIcon />
                </span>
              </button>
            }
          />
        </div>
      </div>
    </>
  )
}

type NavigationSectionProps = {
  tabIndex: number
  title: string
  children: React.ReactNode
}

const NavigationSection = (props: NavigationSectionProps) => (
  <li>
    <Collapsible.Root className={styles.collapse} defaultOpen>
      <Collapsible.Trigger
        className={styles.collapseTrigger}
        tabIndex={props.tabIndex}
      >
        <span className={styles.collapseHeader}>
          {props.title}
          <ChevronSmallDown
            className={styles.collapseIcon}
            role="presentation"
            aria-hidden="true"
          />
        </span>
      </Collapsible.Trigger>

      <Collapsible.Content className={styles.collapseContent}>
        <ul>{props.children}</ul>
      </Collapsible.Content>
    </Collapsible.Root>
  </li>
)

type NavigationSectionItemProps = {
  tabIndex: number
  path: string
  onClick: () => void
  title: string
  action?: React.ReactNode
  className?: string
}

const NavigationSectionItem = (props: NavigationSectionItemProps) => (
  <NavigationMenu.Item className={styles.menuItem}>
    <div className={styles.menuItemContent}>
      <NavigationMenu.Link asChild>
        <Link
          tabIndex={props.tabIndex}
          to={props.path}
          activeProps={{
            className: styles.navigationLinkActive
          }}
          onClick={props.onClick}
          className={clsx(styles.link, props.className)}
        >
          <div className={styles.linkText}>{props.title}</div>
        </Link>
      </NavigationMenu.Link>
      {props.action}
    </div>
  </NavigationMenu.Item>
)

type FavouritesProps = {
  tabIndex: number
  onLinkClick: () => void
}

const Favourites = (props: FavouritesProps) => {
  const { t } = useTranslation('common')
  const { data: favourites, refetch: refetchFavourites } = useFavourites()
  const location = useLocation()

  const { mutate: removeFavourite } = useRemoveFavourite({
    onSuccess: () => {
      refetchFavourites()
      toast({
        variant: 'success',
        title: t('toast.successfullly-removed-from-favourites')
      })
    }
  })

  if (!favourites?.length) return null

  return (
    <NavigationSection
      title={t('navigation.favourites')}
      tabIndex={props.tabIndex}
    >
      {favourites.map(({ id, name, url }) => (
        <NavigationSectionItem
          key={id}
          title={name}
          path={url}
          tabIndex={props.tabIndex}
          onClick={props.onLinkClick}
          action={<FavouriteActions onRemove={() => removeFavourite({ id })} />}
          className={clsx(
            checkIfLocationMatchesString(location, url) &&
              styles.navigationLinkActive,
            styles.favouriteLink
          )}
        />
      ))}
    </NavigationSection>
  )
}

type FavouriteActionsProps = {
  onRemove: () => void
}

const FavouriteActions = (props: FavouriteActionsProps) => {
  const { t } = useTranslation('common')
  return (
    <Dropdown
      trigger={
        <button
          className={styles.dropdownTrigger}
          aria-label={t('button.more')}
        >
          <MoreOutlined />
        </button>
      }
      variant="inverted"
      className={styles.dropdown}
      align="end"
    >
      <DropdownMenuItem
        onClick={props.onRemove}
        className={styles.dropdownItem}
      >
        {t('button.remove')} <DeleteIcon />
      </DropdownMenuItem>
    </Dropdown>
  )
}

const Navigation = () => {
  const { user } = useAuthStore()

  return (
    <NavigationLayout
      items={getItems(user || undefined)}
      withGeneratorItem={user?.isGeneratorAdmin}
    />
  )
}

export default Navigation
